import { toUtcDate } from './bridgeChartTime';
import { getReolinkFileTimestampMs } from './reolinkCameraBattery';

const SEVERITY_RANK = { elevated: 1, significant: 2, critical: 3 };
const AXIS_KEYS = ['xAxis', 'yAxis', 'zAxis'];
const MATCH_WINDOW_MS = 2 * 60 * 1000;

const toId = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    if (value._id != null) return String(value._id);
    return '';
  }
  return String(value);
};

export const normalizeVideoSeverity = (value) => {
  const key = String(value || '').trim().toLowerCase();
  return SEVERITY_RANK[key] ? key : null;
};

export const worstSeverity = (...values) => {
  let worst = null;
  values.forEach((value) => {
    const sev = normalizeVideoSeverity(value);
    if (!sev) return;
    if (!worst || SEVERITY_RANK[sev] > SEVERITY_RANK[worst]) worst = sev;
  });
  return worst;
};

export const getSensorVideoTimestampMs = (video) => {
  const recorded = new Date(video?.recordedAt || 0).getTime();
  const fromName = getReolinkFileTimestampMs(
    video?.fileName || video?.s3Key || video?.filename || video?.name || ''
  );
  const valid = [recorded, fromName].filter((ms) => Number.isFinite(ms) && ms > 0);
  return valid.length ? Math.max(...valid) : 0;
};

const bandBounds = (band, baseline) => {
  if (!band || typeof band !== 'object') return null;
  const lower = Number(band.lower);
  const upper = Number(band.upper);
  if (!Number.isFinite(lower) && !Number.isFinite(upper)) return null;
  const base = Number.isFinite(Number(baseline)) ? Number(baseline) : 0;
  return {
    lower: Number.isFinite(lower) ? base + lower : -Infinity,
    upper: Number.isFinite(upper) ? base + upper : Infinity,
  };
};

const isOutsideBand = (value, band, baseline) => {
  const bounds = bandBounds(band, baseline);
  if (!bounds) return false;
  return value < bounds.lower || value > bounds.upper;
};

/** Nested bands: outside significant → critical, outside elevated → significant, outside normal → elevated. */
export const classifyAxisSeverity = (value, limits, baseline = 0) => {
  if (value == null || value === '' || !limits) return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  if (limits.significant && isOutsideBand(num, limits.significant, baseline)) return 'critical';
  if (limits.elevated && isOutsideBand(num, limits.elevated, baseline)) return 'significant';
  if (limits.normal && isOutsideBand(num, limits.normal, baseline)) return 'elevated';
  return null;
};

const findSensorForReading = (reading, sensors = []) => {
  if (!sensors.length) return null;
  const readingId = toId(reading?.sensorId || reading?._id);
  const readingSerial = reading?.sensorSerialNo != null ? String(reading.sensorSerialNo) : '';
  return sensors.find((sensor) => {
    const id = toId(sensor?._id);
    const serial = sensor?.sensorSerialNo != null ? String(sensor.sensorSerialNo) : '';
    return (readingId && id && readingId === id)
      || (readingSerial && serial && readingSerial === serial)
      || (readingId && serial && readingId === serial);
  }) || (sensors.length === 1 ? sensors[0] : null);
};

const loggerSerialOfSensor = (sensor, dataLoggers = []) => {
  const direct = String(sensor?.dataLoggerSerialNo || '').trim();
  if (direct) return direct;
  const loggerId = toId(sensor?.dataLoggerId);
  if (!loggerId) return '';
  const logger = (dataLoggers || []).find((dl) => toId(dl?._id) === loggerId);
  return String(logger?.serialNo || '').trim();
};

const readingBelongsToVideoLogger = (reading, video, sensors = [], dataLoggers = []) => {
  const videoSerial = String(video?.dataLoggerSerialNo || '').trim();
  if (!videoSerial) return true;
  const readingSerial = String(
    reading?.dataLoggerSerialNo || reading?.dataLoggerSerial || ''
  ).trim();
  if (readingSerial) return readingSerial === videoSerial;
  const sensor = findSensorForReading(reading, sensors);
  if (!sensor) return false;
  return loggerSerialOfSensor(sensor, dataLoggers) === videoSerial;
};

export const classifyReadingSeverity = (reading, sensors = []) => {
  const fromField = normalizeVideoSeverity(reading?.severity);
  if (fromField) return fromField;

  const sensor = findSensorForReading(reading, sensors);
  const spec = sensor?.specLimits;
  if (!spec) return null;

  const baselines = spec.baselineValues || {};
  return worstSeverity(
    ...AXIS_KEYS.map((axis) => classifyAxisSeverity(reading?.[axis], spec[axis], baselines[axis]))
  );
};

const axesForChartView = (axisView) => (
  AXIS_KEYS.includes(axisView) ? [axisView] : AXIS_KEYS
);

const instantMs = (timestamp) => {
  const ms = toUtcDate(timestamp).getTime();
  return Number.isFinite(ms) && ms > 0 ? ms : 0;
};

const wallFormatterCache = new Map();
const wallResultCache = new Map();
const WALL_CACHE_LIMIT = 50000;

const formatterForZone = (timeZone) => {
  let formatter = wallFormatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    wallFormatterCache.set(timeZone, formatter);
  }
  return formatter;
};

/**
 * Clock shown on screen. Event videos print recordedAt as UTC.
 * Chart samples print in the bridge zone. Comparing those two clocks
 * keeps a 5:46 PM video next to a 5:47 PM point.
 */
const wallClockMs = (timestamp, timeZone) => {
  const date = toUtcDate(timestamp);
  const ms = date.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  if (!timeZone || timeZone === 'UTC') return ms;
  const cacheKey = `${timeZone}|${ms}`;
  const cached = wallResultCache.get(cacheKey);
  if (cached != null) return cached;
  let result = ms;
  try {
    const parts = formatterForZone(timeZone).formatToParts(date);
    const get = (type) => Number(parts.find((part) => part.type === type)?.value);
    let hour = get('hour');
    if (hour === 24) hour = 0;
    result = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      hour,
      get('minute'),
      get('second'),
      date.getUTCMilliseconds(),
    );
  } catch {
    result = ms;
  }
  if (wallResultCache.size > WALL_CACHE_LIMIT) wallResultCache.clear();
  wallResultCache.set(cacheKey, result);
  return result;
};

/** Time printed on the Event Video row. */
const videoListClockMs = (video) => {
  const recorded = instantMs(video?.recordedAt);
  if (recorded) return recorded;
  return getSensorVideoTimestampMs(video);
};

const withinMatchWindow = (pointMs, videoMs) => (
  pointMs > 0 && videoMs > 0 && Math.abs(pointMs - videoMs) <= MATCH_WINDOW_MS
);

const finiteSampleValue = (sample) => {
  if (sample == null || sample === '') return null;
  const raw = typeof sample === 'object' ? sample.value : sample;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
};

/** Points on the vertical stem, each with the timestamp the chart tooltip shows. */
const stackPointsForAxis = (reading, axis) => {
  const nested = reading?._axisSamples?.[axis];
  const chartNested = reading?.[`${axis}Samples`];
  const source = Array.isArray(nested) && nested.length
    ? nested
    : (Array.isArray(chartNested) && chartNested.length ? chartNested : null);
  if (source) {
    const points = [];
    source.forEach((sample) => {
      const value = finiteSampleValue(sample);
      if (value == null) return;
      const ownTime = sample && typeof sample === 'object' ? sample.timestamp : null;
      points.push({
        axis,
        value,
        timestamp: ownTime || reading?.timestamp,
      });
    });
    if (points.length) return points;
  }
  const direct = finiteSampleValue(reading?.[axis]);
  if (direct == null) return [];
  return [{ axis, value: direct, timestamp: reading?.timestamp }];
};

const keepWorst = (current, next) => {
  if (!next) return current;
  if (!current || SEVERITY_RANK[next] > SEVERITY_RANK[current]) return next;
  return current;
};

const THRESHOLD_BANDS = ['normal', 'elevated', 'significant'];
const THRESHOLD_SIDES = ['lower', 'upper'];

const finiteOrNull = (value) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const thresholdFieldKey = (axis, band, side) => `${axis}|${band}|${side}`;

const normalizeLogAxis = (axis) => {
  const key = String(axis || '').trim().toLowerCase();
  if (key === 'x' || key === 'xaxis') return 'xAxis';
  if (key === 'y' || key === 'yaxis') return 'yAxis';
  if (key === 'z' || key === 'zaxis') return 'zAxis';
  return AXIS_KEYS.includes(axis) ? axis : null;
};

const normalizeLogBand = (value) => {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'normal') return 'normal';
  if (key === 'elevated' || key === 'elev') return 'elevated';
  if (key === 'significant' || key === 'sig') return 'significant';
  return null;
};

const normalizeLogSide = (value) => {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'lower' || key === 'l') return 'lower';
  if (key === 'upper' || key === 'u') return 'upper';
  return null;
};

const compileSensorTimeline = (sensor, logs) => {
  const spec = sensor?.specLimits || {};
  const current = new Map();
  AXIS_KEYS.forEach((axis) => {
    THRESHOLD_BANDS.forEach((band) => {
      THRESHOLD_SIDES.forEach((side) => {
        current.set(
          thresholdFieldKey(axis, band, side),
          finiteOrNull(spec[axis]?.[band]?.[side]),
        );
      });
    });
  });
  const byKey = new Map();
  (logs || []).forEach((entry) => {
    const axis = normalizeLogAxis(entry?.axis);
    const band = normalizeLogBand(entry?.specLimitType || entry?.spec_limit_type);
    const side = normalizeLogSide(entry?.limitSide || entry?.limit_side);
    const ms = instantMs(entry?.timestamp);
    if (!axis || !band || !side || !ms) return;
    const key = thresholdFieldKey(axis, band, side);
    const change = {
      ms,
      newValue: finiteOrNull(entry?.newValue ?? entry?.new_value),
      oldValue: finiteOrNull(entry?.oldValue ?? entry?.previousValue ?? entry?.old_value),
    };
    if (change.newValue == null && change.oldValue == null) return;
    const list = byKey.get(key) || [];
    list.push(change);
    byKey.set(key, list);
  });
  byKey.forEach((list) => {
    list.sort((a, b) => a.ms - b.ms);
  });
  return { current, byKey, baseline: spec.baselineValues || {} };
};

const thresholdValueAt = (timeline, key, timeMs) => {
  const changes = timeline.byKey.get(key) || [];
  let last = null;
  for (let i = 0; i < changes.length; i += 1) {
    if (changes[i].ms <= timeMs) last = changes[i];
    else break;
  }
  if (last && last.newValue != null) return last.newValue;
  if (!last && changes.length && changes[0].oldValue != null) return changes[0].oldValue;
  return timeline.current.get(key);
};

const axisLimitsAt = (timeline, axis, timeMs) => {
  const limits = {};
  THRESHOLD_BANDS.forEach((band) => {
    const bandLimits = {};
    THRESHOLD_SIDES.forEach((side) => {
      bandLimits[side] = thresholdValueAt(timeline, thresholdFieldKey(axis, band, side), timeMs);
    });
    limits[band] = bandLimits;
  });
  return limits;
};

/** Per-sensor saved threshold changes. Empty logs leave Case 1 on the current limits. */
export const buildSensorThresholdTimelines = (sensors, logsBySensorId) => {
  if (!logsBySensorId) return null;
  const map = new Map();
  let any = false;
  (sensors || []).forEach((sensor) => {
    const id = toId(sensor?._id);
    const serial = sensor?.sensorSerialNo != null ? String(sensor.sensorSerialNo) : '';
    const logs = (id && logsBySensorId[id]) || (serial && logsBySensorId[serial]) || [];
    if (!logs.length) return;
    any = true;
    const timeline = compileSensorTimeline(sensor, logs);
    if (id) map.set(id, timeline);
    if (serial) map.set(serial, timeline);
  });
  return any ? map : null;
};

const timelineForSensor = (sensor, timelines) => {
  if (!timelines || !sensor) return null;
  const id = toId(sensor?._id);
  if (id && timelines.get(id)) return timelines.get(id);
  const serial = sensor?.sensorSerialNo != null ? String(sensor.sensorSerialNo) : '';
  if (serial && timelines.get(serial)) return timelines.get(serial);
  return null;
};

const pushBucket = (buckets, minute, item) => {
  let list = buckets.get(minute);
  if (!list) {
    list = [];
    buckets.set(minute, list);
  }
  list.push(item);
};

/** One pass over the readings. Each video then checks only the ±2 minute buckets. */
const buildSeverityIndex = (
  readings,
  sensors,
  dataLoggers,
  axisView,
  timeZone,
  thresholdTimelines,
) => {
  const axes = axesForChartView(axisView);
  const pointsByMinute = new Map();
  const fallbackByMinute = new Map();
  (readings || []).forEach((reading, readingIndex) => {
    const sensor = findSensorForReading(reading, sensors);
    const spec = sensor?.specLimits;
    const timeline = timelineForSensor(sensor, thresholdTimelines);
    const baselines = (timeline && timeline.baseline) || spec?.baselineValues || {};
    let hasStackPoint = false;
    if (spec) {
      axes.forEach((axis) => {
        stackPointsForAxis(reading, axis).forEach((point) => {
          const pointMs = wallClockMs(point.timestamp, timeZone);
          if (!pointMs) return;
          hasStackPoint = true;
          const sampleInstant = instantMs(point.timestamp) || pointMs;
          pushBucket(pointsByMinute, Math.floor(pointMs / 60000), {
            readingIndex,
            reading,
            ms: pointMs,
            value: point.value,
            limits: timeline ? axisLimitsAt(timeline, axis, sampleInstant) : spec[axis],
            baseline: baselines[axis],
          });
        });
      });
    }
    const readingMs = wallClockMs(reading?.timestamp, timeZone);
    if (!readingMs) return;
    pushBucket(fallbackByMinute, Math.floor(readingMs / 60000), {
      readingIndex,
      reading,
      ms: readingMs,
      hasStackPoint,
    });
  });
  return { pointsByMinute, fallbackByMinute, sensors, dataLoggers };
};

const bucketsInWindow = (videoMs) => {
  const start = Math.floor((videoMs - MATCH_WINDOW_MS) / 60000);
  const end = Math.floor((videoMs + MATCH_WINDOW_MS) / 60000);
  const minutes = [];
  for (let minute = start; minute <= end; minute += 1) minutes.push(minute);
  return minutes;
};

const severityFromIndex = (video, index) => {
  const videoMs = videoListClockMs(video);
  if (!videoMs) return normalizeVideoSeverity(video?.severity);
  const { pointsByMinute, fallbackByMinute, sensors, dataLoggers } = index;
  const seenPointReadings = new Set();
  let anyEvaluated = false;
  let worst = null;
  bucketsInWindow(videoMs).forEach((minute) => {
    const points = pointsByMinute.get(minute);
    if (!points) return;
    points.forEach((point) => {
      if (!withinMatchWindow(point.ms, videoMs)) return;
      if (!readingBelongsToVideoLogger(point.reading, video, sensors, dataLoggers)) return;
      seenPointReadings.add(point.readingIndex);
      anyEvaluated = true;
      worst = keepWorst(
        worst,
        classifyAxisSeverity(point.value, point.limits, point.baseline),
      );
    });
  });
  bucketsInWindow(videoMs).forEach((minute) => {
    const fallbacks = fallbackByMinute.get(minute);
    if (!fallbacks) return;
    fallbacks.forEach((item) => {
      if (seenPointReadings.has(item.readingIndex)) return;
      if (!withinMatchWindow(item.ms, videoMs)) return;
      if (!readingBelongsToVideoLogger(item.reading, video, sensors, dataLoggers)) return;
      worst = keepWorst(worst, normalizeVideoSeverity(item.reading?.severity));
    });
  });
  if (anyEvaluated) return worst;
  if (worst) return worst;
  return normalizeVideoSeverity(video?.severity);
};

/**
 * Severity for a video row from the vertical stack.
 * A sample counts when its displayed time is within ±2 minutes of the video row.
 * Worst point in that window wins: beyond significant → critical,
 * significant band → significant, normal or elevated → yellow.
 * axisView limits the stack to the axis drawn on the graph.
 * timeZone is the bridge zone used by the chart tooltip.
 */
export const severityForVideo = (
  video,
  readings = [],
  sensors = [],
  dataLoggers = [],
  axisView,
  timeZone,
  thresholdTimelines,
) => severityFromIndex(
  video,
  buildSeverityIndex(readings, sensors, dataLoggers, axisView, timeZone, thresholdTimelines),
);

const appearanceClassForSeverity = (severity) => {
  if (severity === 'critical') return 'sev-red';
  if (severity === 'significant') return 'sev-orange';
  return 'sev-yellow';
};

const APPEARANCE_CLASS_RANK = { 'sev-yellow': 1, 'sev-orange': 2, 'sev-red': 3 };

/**
 * Same row colors as eventVideoRowAppearance, indexed once for the whole list.
 * A spike is still scored on its own logger. Within ±2 minutes, every logger's
 * event video then takes the worst of those colors.
 */
export const buildEventVideoAppearanceMap = (
  videos,
  readings = [],
  sensors = [],
  dataLoggers = [],
  axisView,
  timeZone,
  thresholdTimelines,
) => {
  const index = buildSeverityIndex(
    readings,
    sensors,
    dataLoggers,
    axisView,
    timeZone,
    thresholdTimelines,
  );
  const scored = [];
  const seen = new Set();
  (videos || []).forEach((video) => {
    const id = video?._id != null ? String(video._id) : '';
    if (!id || seen.has(id)) return;
    seen.add(id);
    scored.push({
      id,
      ms: videoListClockMs(video),
      className: appearanceClassForSeverity(severityFromIndex(video, index)),
    });
  });
  scored.sort((a, b) => a.ms - b.ms);
  const map = new Map();
  scored.forEach((item, index) => {
    let className = item.className;
    if (item.ms) {
      for (let i = index - 1; i >= 0; i -= 1) {
        const other = scored[i];
        if (!other.ms || item.ms - other.ms > MATCH_WINDOW_MS) break;
        if (APPEARANCE_CLASS_RANK[other.className] > (APPEARANCE_CLASS_RANK[className] || 0)) {
          className = other.className;
        }
      }
      for (let i = index + 1; i < scored.length; i += 1) {
        const other = scored[i];
        if (!other.ms || other.ms - item.ms > MATCH_WINDOW_MS) break;
        if (APPEARANCE_CLASS_RANK[other.className] > (APPEARANCE_CLASS_RANK[className] || 0)) {
          className = other.className;
        }
      }
    }
    const background = SEVERITY_ROW_BACKGROUND[className];
    map.set(item.id, {
      className,
      style: background ? { background } : undefined,
    });
  });
  return map;
};

/** Event Video only: normal + elevated → yellow, significant → orange, critical → red. */
export const videoRowSeverityClass = (
  video,
  readings = [],
  sensors = [],
  dataLoggers = [],
  axisView,
  timeZone,
  thresholdTimelines,
) => {
  const severity = severityForVideo(
    video,
    readings,
    sensors,
    dataLoggers,
    axisView,
    timeZone,
    thresholdTimelines,
  );
  if (severity === 'critical') return 'sev-red';
  if (severity === 'significant') return 'sev-orange';
  return 'sev-yellow';
};

/** Same hex values as `.sensor-video-severity-table` row classes. */
const SEVERITY_ROW_BACKGROUND = {
  'sev-yellow': '#e6b800',
  'sev-orange': '#e07a18',
  'sev-red': '#c94a4a',
};

const IMPACT_COLOR_CLASS = {
  YELLOW: 'sev-yellow',
  ORANGE: 'sev-orange',
  RED: 'sev-red',
};

const IMPACT_CLASSIFICATION_CLASS = {
  NO_IMPACT: 'sev-yellow',
  MINOR_IMPACT: 'sev-orange',
  CRITICAL_IMPACT: 'sev-red',
};

/** Confirmed fender/structure hits. Inner noise types (NORMAL_MOVEMENT) are not impacts. */
const IMPACT_CLASSIFICATIONS = new Set([
  'MINOR_IMPACT',
  'CRITICAL_IMPACT',
  'MAJOR_IMPACT',
]);

const isImpactFieldPresent = (value) => value != null && String(value).trim() !== '';

const foldImpactKey = (name) => String(name || '').toLowerCase().replace(/_/g, '');

const normalizeImpactToken = (value) => (
  isImpactFieldPresent(value) ? String(value).trim().toUpperCase() : ''
);

const readOwnFieldIgnoreCase = (obj, name) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return undefined;
  if (isImpactFieldPresent(obj[name])) return obj[name];
  const wanted = foldImpactKey(name);
  const key = Object.keys(obj).find((k) => foldImpactKey(k) === wanted);
  if (key == null) return undefined;
  return isImpactFieldPresent(obj[key]) ? obj[key] : undefined;
};

const impactFieldSources = (video) => {
  if (!video || typeof video !== 'object') return [];
  return [
    video,
    video.event_result,
    video.eventResult,
    video.impact,
  ].filter((src) => src && typeof src === 'object' && !Array.isArray(src));
};

const readImpactField = (video, name) => {
  const sources = impactFieldSources(video);
  for (let i = 0; i < sources.length; i += 1) {
    const value = readOwnFieldIgnoreCase(sources[i], name);
    if (isImpactFieldPresent(value)) return value;
  }
  return null;
};

const evidenceSummaryOf = (video) => {
  const summary = video?.evidenceSummary || video?.evidence_summary;
  if (!summary || typeof summary !== 'object' || Array.isArray(summary)) return null;
  return summary;
};

const isFalsePositiveSuppressedFlag = (video) => {
  const direct = readImpactField(video, 'false_positive_suppressed');
  if (direct === true || String(direct || '').trim().toLowerCase() === 'true') return true;
  const summary = evidenceSummaryOf(video);
  if (!summary) return false;
  const flag = summary.false_positive_suppressed;
  return flag === true || String(flag || '').trim().toLowerCase() === 'true';
};

const analysisHasFinished = (video) => {
  if (readImpactField(video, 'impactAnalyzedAt')) return true;
  if (readImpactField(video, 'impactClassifierVersion')) return true;
  const summary = evidenceSummaryOf(video);
  if (!summary) return false;
  return Object.keys(summary).length > 0;
};

const flagTrue = (value) => (
  value === true || String(value || '').trim().toLowerCase() === 'true'
);

export const isEventVideoReview = (video) => {
  if (!analysisHasFinished(video)) return false;
  const summary = evidenceSummaryOf(video);
  if (!summary) return false;
  if (summary.review_required === true || String(summary.review_required).toLowerCase() === 'true') {
    return true;
  }
  return String(summary.parsonage_visual_reason || '') === 'insufficient_footage';
};

const isOwnImpactFlag = (video) => {
  if (flagTrue(readImpactField(video, 'own_impact'))) return true;
  const summary = evidenceSummaryOf(video);
  return flagTrue(summary?.own_impact);
};

export const isCrossLoggerConfirmedImpact = (video) => {
  if (!analysisHasFinished(video)) return false;
  const classification = normalizeImpactToken(readImpactField(video, 'classification'));
  if (!IMPACT_CLASSIFICATIONS.has(classification)) return false;
  if (isOwnImpactFlag(video)) return false;
  const direct = readImpactField(video, 'cross_logger_confirmed');
  if (flagTrue(direct)) return true;
  const summary = evidenceSummaryOf(video);
  return flagTrue(summary?.cross_logger_confirmed);
};

/**
 * After Python analysis: noise / non-impacts stay in the list as false positives.
 * Unanalyzed clips are not labeled (API defaults classification to NO_IMPACT).
 */
export const isAnalyzedEventVideoFalsePositive = (video) => {
  if (!analysisHasFinished(video)) return false;
  if (isEventVideoReview(video)) return false;
  const classification = normalizeImpactToken(readImpactField(video, 'classification'));
  if (IMPACT_CLASSIFICATIONS.has(classification)) return false;
  if (isFalsePositiveSuppressedFlag(video)) return true;
  return true;
};

/** Display only: this Event Video's own hit, or a 120s cross-logger hit. */
export const isAnalyzedEventVideoImpact = (video) => {
  if (!analysisHasFinished(video)) return false;
  const classification = normalizeImpactToken(readImpactField(video, 'classification'));
  return IMPACT_CLASSIFICATIONS.has(classification);
};

export const eventVideoListLabel = (video) => {
  const serial = String(video?.dataLoggerSerialNo || '').trim();
  const base = serial ? `${serial}_event` : 'event';
  if (isEventVideoReview(video)) return `${base} (review)`;
  if (isAnalyzedEventVideoFalsePositive(video)) return `${base} (false positive)`;
  if (isAnalyzedEventVideoImpact(video)) return `${base} (confirmed impact)`;
  return base;
};

/** Event Video list: keep every clip, including analyzed non-impacts (false positives). */
export const shouldShowEventVideoInImpactList = () => true;

export const eventVideoImpactRowClass = (video) => {
  const fromColor = IMPACT_COLOR_CLASS[normalizeImpactToken(readImpactField(video, 'color'))];
  if (fromColor) return fromColor;
  const fromClassification = IMPACT_CLASSIFICATION_CLASS[
    normalizeImpactToken(readImpactField(video, 'classification'))
  ];
  if (fromClassification) return fromClassification;
  return 'sev-yellow';
};

export const eventVideoImpactRowStyle = (video) => {
  const impactClass = eventVideoImpactRowClass(video);
  const background = SEVERITY_ROW_BACKGROUND[impactClass];
  return background ? { background } : undefined;
};

/** Event Video rows: live-graph threshold dips only (no model / impact classification). */
export const eventVideoRowClass = (
  video,
  readings = [],
  sensors = [],
  dataLoggers = [],
  axisView,
  timeZone,
  thresholdTimelines,
) => videoRowSeverityClass(
  video,
  readings,
  sensors,
  dataLoggers,
  axisView,
  timeZone,
  thresholdTimelines,
);

export const eventVideoRowAppearance = (
  video,
  readings = [],
  sensors = [],
  dataLoggers = [],
  axisView,
  timeZone,
  thresholdTimelines,
) => {
  const className = eventVideoRowClass(
    video,
    readings,
    sensors,
    dataLoggers,
    axisView,
    timeZone,
    thresholdTimelines,
  );
  const background = SEVERITY_ROW_BACKGROUND[className];
  return {
    className,
    style: background ? { background } : undefined,
  };
};
